import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Trash, Printer, Buildings, PencilSimple, Check, UploadSimple } from "@phosphor-icons/react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";

const DED_LABELS = { admin_fee: "Admin fee %", staff_meal: "Staff meal %", breakages: "Breakages %", other: "Other %" };
const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("stab") || "business";
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [logoTs, setLogoTs] = useState(Date.now());
  const [newCat, setNewCat] = useState({ name: "", department: "Food" });
  const [venues, setVenues] = useState([]);
  const [newVenue, setNewVenue] = useState("");
  const [renaming, setRenaming] = useState(null); // {id, name}

  const loadVenues = () => api.get("/venues").then((r) => setVenues(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => toast.error("Failed to load settings"));
    loadVenues();
  }, []);

  const [pwForm, setPwForm] = useState({ current: "", next: "", confirm: "" });
  const [pwSaving, setPwSaving] = useState(false);

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwForm.next !== pwForm.confirm) return toast.error("New passwords do not match");
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwForm.current, new_password: pwForm.next });
      toast.success("Password updated — use it next time you sign in");
      setPwForm({ current: "", next: "", confirm: "" });
    } catch (err) {
      toast.error(apiError(err, "Could not change password"));
    } finally {
      setPwSaving(false);
    }
  };

  const addVenue = async () => {
    if (!newVenue.trim()) return;
    try {
      await api.post("/venues", { name: newVenue.trim() });
      toast.success(`Venue "${newVenue.trim()}" created — it now appears in the header switcher`);
      setNewVenue("");
      window.location.reload(); // owner gains access to the new venue; refresh picks it up
    } catch (e) {
      toast.error(apiError(e, "Could not create venue"));
    }
  };

  const renameVenue = async () => {
    try {
      await api.put(`/venues/${renaming.id}`, { name: renaming.name.trim() });
      toast.success("Venue renamed");
      setRenaming(null);
      loadVenues();
    } catch (e) {
      toast.error(apiError(e, "Rename failed"));
    }
  };

  if (!s) return <p className="text-muted-foreground">Loading…</p>;

  const distSum = Object.values(s.distribution_pcts).reduce((a, b) => a + (Number(b) || 0), 0);

  const payload = () => ({
    deduction_pcts: Object.fromEntries(Object.entries(s.deduction_pcts).map(([k, v]) => [k, Number(v) || 0])),
    distribution_pcts: Object.fromEntries(Object.entries(s.distribution_pcts).map(([k, v]) => [k, Number(v) || 0])),
    auto_clockout_time: s.auto_clockout_time,
    payroll_day: Number(s.payroll_day) || 25,
    weekly_hours_cap: Number(s.weekly_hours_cap) || 45,
    leave_notify_email: (s.leave_notify_email || "").trim(),
    invoice_categories: s.invoice_categories,
    ocr_auto_post: !!s.ocr_auto_post,
    printer_host: (s.printer_host || "").trim(),
    printer_port: Number(s.printer_port) || 9100,
    printer_mode: s.printer_mode || "direct",
    business_name: s.business_name || "",
    business_reg: s.business_reg || "",
    business_vat: s.business_vat || "",
    business_address: s.business_address || "",
    business_email: s.business_email || "",
    business_phone: s.business_phone || "",
    banking: s.banking || {},
    invoice_terms: s.invoice_terms || "",
    payment_terms_days: Number(s.payment_terms_days) || 14,
    invoice_prefix: s.invoice_prefix || "",
    invoice_next_seq: Number(s.invoice_next_seq) || 1,
    vat_rate: s.vat_rate === "" || s.vat_rate == null ? 15 : Number(s.vat_rate),
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", payload());
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (f) => {
    if (!f) return;
    try {
      const fd = new FormData();
      fd.append("file", f);
      await api.post("/settings/logo", fd);
      setS({ ...s, has_logo: true });
      setLogoTs(Date.now());
      toast.success("Logo uploaded — it now appears on client invoices");
    } catch (e) {
      toast.error(apiError(e, "Logo upload failed"));
    }
  };

  const set = (k, v) => setS({ ...s, [k]: v });
  const setBank = (k, v) => setS({ ...s, banking: { ...(s.banking || {}), [k]: v } });

  const pctInput = (group, key, label) => (
    <div key={key} className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      <Input type="number" step="any" min="0" max="100" data-testid={`setting-${group}-${key}`}
        value={s[group][key]}
        onChange={(e) => setS({ ...s, [group]: { ...s[group], [key]: e.target.value } })} />
    </div>
  );

  const SaveBtn = () => (
    <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="bg-primary hover:bg-[#8B5CF6] text-white">
      {saving ? "Saving…" : "Save settings"}
    </Button>
  );

  return (
    <div className="space-y-6 max-w-4xl" data-testid="settings-page">
      <div>
        <h1 className="font-heading text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground mt-1">Owner-only — changes are recorded in the audit log. Settings apply to the venue selected in the header.</p>
      </div>

      <Tabs value={tab} onValueChange={(v) => setParams({ stab: v })}>
        <TabsList className="flex-wrap h-auto" data-testid="settings-tabs">
          <TabsTrigger value="business" data-testid="stab-business">Business Profile</TabsTrigger>
          <TabsTrigger value="invoicing" data-testid="stab-invoicing">Banking &amp; Invoicing</TabsTrigger>
          <TabsTrigger value="tips" data-testid="stab-tips">Tips</TabsTrigger>
          <TabsTrigger value="operations" data-testid="stab-operations">Operations</TabsTrigger>
          <TabsTrigger value="categories" data-testid="stab-categories">Invoice Categories</TabsTrigger>
          <TabsTrigger value="printer" data-testid="stab-printer">Printer</TabsTrigger>
          <TabsTrigger value="venues" data-testid="stab-venues">Venues</TabsTrigger>
          <TabsTrigger value="hr" data-testid="stab-hr">HR Documents</TabsTrigger>
          <TabsTrigger value="security" data-testid="stab-security">Security</TabsTrigger>
        </TabsList>

        <TabsContent value="security" className="mt-4 space-y-6">
          <Card className="border-border shadow-none" data-testid="security-card">
            <CardHeader><CardTitle className="font-heading text-lg">Change your password</CardTitle>
              <p className="text-xs text-muted-foreground">Applies to your sign-in on every device. If you've forgotten your current password, sign out and use "Forgot your password?" on the sign-in page.</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={changePassword} className="max-w-sm space-y-4" data-testid="change-password-form">
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="pw-current">Current password</Label>
                  <Input id="pw-current" type="password" required autoComplete="current-password" data-testid="pw-current"
                    value={pwForm.current} onChange={(e) => setPwForm({ ...pwForm, current: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="pw-next">New password</Label>
                  <Input id="pw-next" type="password" required minLength={8} autoComplete="new-password" data-testid="pw-next"
                    value={pwForm.next} onChange={(e) => setPwForm({ ...pwForm, next: e.target.value })} placeholder="At least 8 characters" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs" htmlFor="pw-confirm">Repeat new password</Label>
                  <Input id="pw-confirm" type="password" required minLength={8} autoComplete="new-password" data-testid="pw-confirm"
                    value={pwForm.confirm} onChange={(e) => setPwForm({ ...pwForm, confirm: e.target.value })} />
                </div>
                <Button type="submit" disabled={pwSaving} data-testid="pw-submit" className="bg-primary hover:bg-[#8B5CF6] text-white">
                  {pwSaving ? "Updating…" : "Update password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="business" className="mt-4 space-y-6">
          <Card className="border-border shadow-none" data-testid="business-card">
            <CardHeader><CardTitle className="font-heading text-lg">Business profile</CardTitle>
              <p className="text-xs text-muted-foreground">Printed on client invoices for this venue. Each venue has its own profile.</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-5">
                {s.has_logo ? (
                  <img src={`${API_BASE}/settings/logo?ts=${logoTs}`} alt="Business logo" data-testid="business-logo-preview"
                    className="h-16 w-16 object-contain rounded-md border border-border bg-white p-1" />
                ) : (
                  <div className="h-16 w-16 rounded-md border border-dashed border-border flex items-center justify-center text-muted-foreground text-[10px] text-center" data-testid="business-logo-empty">No logo</div>
                )}
                <div>
                  <label className="cursor-pointer">
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="business-logo-input"
                      onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} />
                    <span className="inline-flex items-center gap-1.5 text-sm border border-border rounded-md px-3 py-2 hover:bg-accent transition-colors">
                      <UploadSimple size={15} /> Upload logo (JPG/PNG, max 5MB)
                    </span>
                  </label>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label className="text-xs">Legal / trading name</Label><Input data-testid="business-name" value={s.business_name || ""} onChange={(e) => set("business_name", e.target.value)} placeholder="e.g. Opá! Greek Street Food (Pty) Ltd" /></div>
                <div className="space-y-1.5"><Label className="text-xs">Registration number</Label><Input data-testid="business-reg" value={s.business_reg || ""} onChange={(e) => set("business_reg", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">VAT number</Label><Input data-testid="business-vat" value={s.business_vat || ""} onChange={(e) => set("business_vat", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Email</Label><Input type="email" data-testid="business-email" value={s.business_email || ""} onChange={(e) => set("business_email", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Phone</Label><Input data-testid="business-phone" value={s.business_phone || ""} onChange={(e) => set("business_phone", e.target.value)} /></div>
                <div className="space-y-1.5"><Label className="text-xs">Address</Label><Textarea rows={2} data-testid="business-address" value={s.business_address || ""} onChange={(e) => set("business_address", e.target.value)} /></div>
              </div>
              <SaveBtn />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invoicing" className="mt-4 space-y-6">
          <Card className="border-border shadow-none" data-testid="banking-card">
            <CardHeader><CardTitle className="font-heading text-lg">Banking details</CardTitle>
              <p className="text-xs text-muted-foreground">Preset on every client invoice for this venue (editable per invoice).</p>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Bank</Label><Input data-testid="banking-bank-name" value={s.banking?.bank_name || ""} onChange={(e) => setBank("bank_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Account name</Label><Input data-testid="banking-account-name" value={s.banking?.account_name || ""} onChange={(e) => setBank("account_name", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Account number</Label><Input data-testid="banking-account-number" value={s.banking?.account_number || ""} onChange={(e) => setBank("account_number", e.target.value)} /></div>
              <div className="space-y-1.5"><Label className="text-xs">Branch code</Label><Input data-testid="banking-branch-code" value={s.banking?.branch_code || ""} onChange={(e) => setBank("branch_code", e.target.value)} /></div>
            </CardContent>
          </Card>

          <Card className="border-border shadow-none" data-testid="invoice-terms-card">
            <CardHeader><CardTitle className="font-heading text-lg">Invoice terms &amp; numbering</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-xs">Invoice prefix</Label>
                  <Input data-testid="invoice-prefix" value={s.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} placeholder="e.g. OPA" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Next invoice number</Label>
                  <Input type="number" min="1" data-testid="invoice-next-seq" value={s.invoice_next_seq ?? 1} onChange={(e) => set("invoice_next_seq", e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">Next invoice: <strong>{s.invoice_prefix || "…"}-{String(s.invoice_next_seq || 1).padStart(3, "0")}</strong></p>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Default payment terms (days)</Label>
                  <Input type="number" min="0" data-testid="payment-terms-days" value={s.payment_terms_days ?? 14} onChange={(e) => set("payment_terms_days", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">VAT rate %</Label>
                  <Input type="number" step="any" min="0" max="100" data-testid="vat-rate" value={s.vat_rate ?? 15} onChange={(e) => set("vat_rate", e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">Prices are VAT-inclusive — VAT is worked out of the total, not added.</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Terms note (printed on invoices)</Label>
                <Textarea rows={3} data-testid="invoice-terms" value={s.invoice_terms || ""} onChange={(e) => set("invoice_terms", e.target.value)}
                  placeholder={"e.g. A 50% deposit secures your booking.\nBalance payable on the evening of the function."} />
              </div>
              <SaveBtn />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="tips" className="mt-4 space-y-6">
          <Card className="border-border shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg">Tip deductions (%)</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.keys(DED_LABELS).map((k) => pctInput("deduction_pcts", k, DED_LABELS[k]))}
            </CardContent>
          </Card>

          <Card className="border-border shadow-none">
            <CardHeader>
              <CardTitle className="font-heading text-lg">Tip distribution weightings (%)</CardTitle>
              <p className={`text-xs ${Math.abs(distSum - 100) > 0.01 ? "text-[#F87171]" : "text-muted-foreground"}`} data-testid="dist-sum">
                Currently sums to {distSum}% {Math.abs(distSum - 100) > 0.01 && "— should total 100%"}
              </p>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {Object.entries(s.tip_categories).map(([k, label]) => pctInput("distribution_pcts", k, `${label} %`))}
            </CardContent>
          </Card>
          <SaveBtn />
        </TabsContent>

        <TabsContent value="operations" className="mt-4 space-y-6">
          <Card className="border-border shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg">Operations</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="space-y-1.5">
                <Label className="text-xs">Automatic clock-out time</Label>
                <Input type="time" data-testid="setting-auto-clockout" value={s.auto_clockout_time}
                  onChange={(e) => set("auto_clockout_time", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Payroll day of month</Label>
                <Input type="number" min="1" max="28" data-testid="setting-payroll-day" value={s.payroll_day}
                  onChange={(e) => set("payroll_day", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Leave request notifications email</Label>
                <Input type="email" placeholder="e.g. manager@yourvenue.co.za (blank = off)" data-testid="setting-leave-notify-email"
                  value={s.leave_notify_email || ""}
                  onChange={(e) => set("leave_notify_email", e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Weekly hours cap (overtime flag)</Label>
                <Input type="number" min="1" max="80" data-testid="setting-weekly-hours-cap" value={s.weekly_hours_cap ?? 45}
                  onChange={(e) => set("weekly_hours_cap", e.target.value)} />
                <p className="text-xs text-muted-foreground">Scheduled or clocked hours above this are flagged as overtime (default 45h, BCEA).</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">UIF &amp; PAYE</Label>
                <p className="text-xs text-muted-foreground border border-dashed border-border rounded-md px-3 py-2.5">
                  Captured manually per employee in each payroll run (by your accountant).
                </p>
              </div>
            </CardContent>
          </Card>
          <SaveBtn />
        </TabsContent>

        <TabsContent value="categories" className="mt-4 space-y-6">
          <Card className="border-border shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg">Invoice categories</CardTitle>
              <p className="text-xs text-muted-foreground">Department tags drive the cost-of-sales report (Food % vs food sales, Beverage % vs beverage sales, Other % vs total revenue)</p>
            </CardHeader>
            <CardContent className="space-y-2">
              <label className="flex items-start gap-3 rounded-md border border-border bg-muted/40 px-3 py-2.5 cursor-pointer" data-testid="ocr-auto-post-row">
                <input type="checkbox" className="mt-0.5" data-testid="setting-ocr-auto-post"
                  checked={!!s.ocr_auto_post}
                  onChange={(e) => set("ocr_auto_post", e.target.checked)} />
                <span className="text-sm">
                  <span className="font-medium">Auto-post fully-matched OCR invoices</span>
                  <span className="block text-xs text-muted-foreground">
                    Off (default): every uploaded invoice goes to the review queue so you can check line items and stock matches before anything posts to inventory.
                    On: a fully-matched, high-confidence repeat invoice posts straight to inventory without review.
                  </span>
                </span>
              </label>
              {(s.invoice_categories || []).map((c) => (
                <div key={c.id} className="flex items-center gap-3 border-b last:border-0 pb-2" data-testid={`cat-row-${c.id}`}>
                  <Input className="h-8 flex-1" value={c.name} data-testid={`cat-name-${c.id}`}
                    onChange={(e) => setS({ ...s, invoice_categories: s.invoice_categories.map((x) => x.id === c.id ? { ...x, name: e.target.value } : x) })} />
                  <Select value={c.department}
                    onValueChange={(v) => setS({ ...s, invoice_categories: s.invoice_categories.map((x) => x.id === c.id ? { ...x, department: v } : x) })}>
                    <SelectTrigger className="w-36 h-8" data-testid={`cat-dept-${c.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Food">Food</SelectItem>
                      <SelectItem value="Beverage">Beverage</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" data-testid={`cat-delete-${c.id}`}
                    onClick={() => setS({ ...s, invoice_categories: s.invoice_categories.filter((x) => x.id !== c.id) })}>
                    <Trash size={15} />
                  </Button>
                </div>
              ))}
              <div className="flex items-center gap-3 pt-2">
                <Input className="h-9 flex-1" placeholder="New category name" data-testid="cat-add-name" value={newCat.name}
                  onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} />
                <Select value={newCat.department} onValueChange={(v) => setNewCat({ ...newCat, department: v })}>
                  <SelectTrigger className="w-36 h-9" data-testid="cat-add-dept"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Food">Food</SelectItem>
                    <SelectItem value="Beverage">Beverage</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" className="h-9" data-testid="cat-add-btn"
                  onClick={() => {
                    if (!newCat.name.trim()) return toast.error("Category name required");
                    setS({ ...s, invoice_categories: [...(s.invoice_categories || []), { id: `cat-${Date.now()}`, name: newCat.name.trim(), department: newCat.department }] });
                    setNewCat({ name: "", department: "Food" });
                  }}>
                  <Plus size={15} className="mr-1" /> Add
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground">Remember to hit "Save settings" to persist category changes.</p>
            </CardContent>
          </Card>
          <SaveBtn />
        </TabsContent>

        <TabsContent value="printer" className="mt-4 space-y-6">
          <Card className="border-border shadow-none">
            <CardHeader><CardTitle className="font-heading text-lg">Receipt printer (ESC/POS)</CardTitle>
              <p className="text-xs text-muted-foreground">
                Thermal printer for receipts (Epson TM-series or compatible). <strong>Direct</strong> sends from the cloud to a public/static IP.
                <strong> Bridge</strong> is for a normal LAN-only printer (192.168.x.x): a small device on the venue network polls for jobs — no port-forwarding needed.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 items-end">
                <div className="space-y-1.5">
                  <Label className="text-xs">Mode</Label>
                  <Select value={s.printer_mode || "direct"} onValueChange={(v) => set("printer_mode", v)}>
                    <SelectTrigger data-testid="setting-printer-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="direct">Direct (public/static IP)</SelectItem>
                      <SelectItem value="bridge">Bridge (LAN printer via on-site bridge)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Printer IP address</Label>
                  <Input placeholder={s.printer_mode === "bridge" ? "LAN IP, e.g. 192.168.1.50" : "e.g. 41.x.x.x (blank = browser print)"}
                    data-testid="setting-printer-host" value={s.printer_host || ""}
                    onChange={(e) => set("printer_host", e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs">Port</Label>
                  <Input type="number" min="1" max="65535" data-testid="setting-printer-port" value={s.printer_port ?? 9100}
                    onChange={(e) => set("printer_port", e.target.value)} />
                  <p className="text-[11px] text-muted-foreground">9100 is the standard RAW/JetDirect port.</p>
                </div>
              </div>

              {(s.printer_mode || "direct") === "bridge" && (
                <div className="rounded-md border border-border bg-muted/40 p-4 space-y-3" data-testid="bridge-panel">
                  <div className="space-y-1.5">
                    <Label className="text-xs">Print key (goes in the bridge script — keep it private)</Label>
                    <div className="flex gap-2">
                      <Input readOnly value={s.print_key || ""} placeholder="No key yet — generate one"
                        data-testid="setting-print-key" className="font-mono text-xs" />
                      <Button variant="outline" data-testid="print-key-generate"
                        onClick={async () => {
                          try {
                            const { data } = await api.post("/settings/printer/regenerate-key");
                            setS({ ...s, print_key: data.print_key });
                            toast.success(s.print_key ? "New key generated — update the bridge script" : "Print key generated");
                          } catch (e) { toast.error(apiError(e, "Key generation failed")); }
                        }}>
                        {s.print_key ? "Regenerate" : "Generate"}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Bridge setup: copy <code className="font-mono">bridge/print_bridge.py</code> onto any always-on device on the venue
                    network (Raspberry Pi, laptop, old PC), fill in the app URL, this key and the printer IP, and run it with Python 3.
                    Full steps are in the file header. Then use "Send test print" below.
                  </p>
                  {s.last_print && (
                    <p className="text-xs" data-testid="last-print-status">
                      Last print ({s.last_print.ref || s.last_print.kind}):{" "}
                      <strong className={s.last_print.status === "done" ? "text-[#A9C97E]" : s.last_print.status === "failed" ? "text-destructive" : ""}>
                        {s.last_print.status === "done" ? "printed ✓" : s.last_print.status === "failed" ? `failed — ${s.last_print.result || "printer unreachable"}` : s.last_print.status}
                      </strong>
                      {" · "}{(s.last_print.done_at || s.last_print.created_at || "").slice(11, 16)}
                    </p>
                  )}
                </div>
              )}

              <div>
                <Button variant="outline" data-testid="printer-test-btn"
                  disabled={(s.printer_mode || "direct") === "direct" ? !s.printer_host : false}
                  onClick={async () => {
                    try {
                      await api.put("/settings", payload());
                      const { data } = await api.post("/settings/printer/test");
                      if (data.queued) toast.success("Test ticket queued — the bridge will print it within a few seconds");
                      else toast.success(`Test ticket sent to ${data.host}:${data.port}`);
                    } catch (e) {
                      toast.error(apiError(e, "Test print failed"));
                    }
                  }}>
                  <Printer size={15} className="mr-1" /> Send test print
                </Button>
              </div>
              <SaveBtn />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="venues" className="mt-4 space-y-6">
          <Card className="border-border shadow-none" data-testid="venues-card">
            <CardHeader><CardTitle className="font-heading text-lg flex items-center gap-2"><Buildings size={18} /> Venues</CardTitle>
              <p className="text-xs text-muted-foreground">Each venue has its own staff, rosters, stock, recipes, finances and settings. Switch the active venue in the header.</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {venues.map((v) => (
                <div key={v.id} className="flex items-center gap-2" data-testid={`venue-row-${v.id}`}>
                  {renaming?.id === v.id ? (
                    <>
                      <Input value={renaming.name} onChange={(e) => setRenaming({ ...renaming, name: e.target.value })} className="max-w-xs" data-testid="venue-rename-input" />
                      <Button size="sm" variant="outline" onClick={renameVenue} data-testid="venue-rename-save"><Check size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => setRenaming(null)}>Cancel</Button>
                    </>
                  ) : (
                    <>
                      <span className="font-medium text-sm flex-1">{v.name}</span>
                      <Button size="sm" variant="ghost" onClick={() => setRenaming({ id: v.id, name: v.name })} data-testid={`venue-rename-${v.id}`}>
                        <PencilSimple size={14} />
                      </Button>
                    </>
                  )}
                </div>
              ))}
              <div className="flex items-center gap-2 pt-1">
                <Input placeholder="New venue name (e.g. Dusk — Umhlanga)" value={newVenue}
                  onChange={(e) => setNewVenue(e.target.value)} className="max-w-xs" data-testid="venue-new-name" />
                <Button variant="outline" onClick={addVenue} data-testid="venue-add-btn"><Plus size={14} className="mr-1" /> Add venue</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hr" className="mt-4">
          <HRTemplatesPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const HR_DOC_LABELS = { offer_letter: "Offer letter", contract_permanent: "Contract — Permanent", contract_fixed_term: "Contract — Fixed term" };
const SAMPLE_FIELDS = {
  company_name: "Your Restaurant (Pty) Ltd", company_address: "1 Main Road, Cape Town", company_phone: "021 555 0100",
  company_email: "info@yourrestaurant.co.za", venue_name: "Your Venue", date: "2026-09-21",
  candidate_name: "Jane Candidate", employee_name: "Jane Candidate", employee_id_number: "9001015800080",
  employee_address: "7 Oak Avenue, Cape Town", employee_phone: "082 555 0100", position: "Waiter",
  start_date: "2026-10-01", end_date: "2027-09-30", probation_period: "3 months", salary: "R 35.00/hour",
  salary_type: "hourly wage", reporting_manager: "Floor Manager", working_hours: "5 shifts per week, per roster",
  working_hours_detail: "Monday to Saturday as per the weekly roster, max 45 ordinary hours per week.",
  bank_name: "FNB", account_number: "62123456789", branch_code: "250655", account_type: "Cheque",
  signee_name: "Owner Name", signee_title: "Owner",
};

function HRTemplatesPanel() {
  const [tpls, setTpls] = useState(null);
  const [fields, setFields] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [edit, setEdit] = useState(null); // {id?, doc_type, name, body}
  const [preview, setPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef(null);

  const load = () => {
    api.get("/hr-templates").then((r) => setTpls(r.data)).catch(() => toast.error("Failed to load HR templates"));
    api.get("/hr-templates/merge-fields").then((r) => { setFields(r.data.fields); setDocTypes(r.data.doc_types); }).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      if (edit.id) await api.put(`/hr-templates/${edit.id}`, edit);
      else await api.post("/hr-templates", edit);
      toast.success("Template saved");
      setEdit(null);
      load();
    } catch (e) {
      toast.error(apiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const restore = async (t) => {
    try {
      await api.post(`/hr-templates/${t.id}/restore-default`);
      toast.success("Restored to the built-in default");
      load();
    } catch (e) {
      toast.error(apiError(e, "Restore failed"));
    }
  };

  const insertField = (f) => {
    const el = bodyRef.current;
    const tag = `{{${f}}}`;
    if (!el) return setEdit({ ...edit, body: edit.body + tag });
    const s = el.selectionStart ?? edit.body.length;
    const next = edit.body.slice(0, s) + tag + edit.body.slice(el.selectionEnd ?? s);
    setEdit({ ...edit, body: next });
    requestAnimationFrame(() => { el.focus(); el.selectionStart = el.selectionEnd = s + tag.length; });
  };

  const previewText = edit ? Object.entries(SAMPLE_FIELDS).reduce((t, [k, v]) => t.split(`{{${k}}}`).join(v), edit.body) : "";

  if (!tpls) return <p className="text-muted-foreground">Loading…</p>;

  if (edit) {
    return (
      <Card className="border-border shadow-none" data-testid="hr-template-editor">
        <CardHeader><CardTitle className="font-heading text-lg">{edit.id ? "Edit template" : "New template"}</CardTitle>
          <p className="text-xs text-muted-foreground">Plain text with merge fields — rendered to PDF when the document is generated. Leave/notice figures reference the BCEA / Sectoral Determination 14 defaults; edit to suit your venue.</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Template name</Label>
              <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="hr-tpl-name" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Document type</Label>
              <Select value={edit.doc_type} onValueChange={(v) => setEdit({ ...edit, doc_type: v })} disabled={!!edit.id}>
                <SelectTrigger data-testid="hr-tpl-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {docTypes.map((d) => <SelectItem key={d} value={d}>{HR_DOC_LABELS[d] || d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Merge fields — click to insert</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="hr-merge-fields">
              {fields.map((f) => (
                <button key={f} type="button" onClick={() => insertField(f)} data-testid={`hr-field-${f}`}
                  className="text-[11px] rounded-full border border-border px-2.5 py-1 hover:bg-accent transition-colors">{`{{${f}}}`}</button>
              ))}
            </div>
          </div>
          <Textarea ref={bodyRef} rows={22} value={edit.body} onChange={(e) => setEdit({ ...edit, body: e.target.value })}
            className="font-mono text-xs leading-relaxed" data-testid="hr-tpl-body" />
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={save} disabled={saving || !edit.name.trim() || !edit.body.trim()} data-testid="hr-tpl-save"
              className="bg-primary hover:bg-[#8B5CF6] text-white">{saving ? "Saving…" : "Save template"}</Button>
            <Button variant="outline" onClick={() => setPreview(!preview)} data-testid="hr-tpl-preview">
              {preview ? "Hide preview" : "Preview with sample data"}
            </Button>
            <Button variant="ghost" onClick={() => setEdit(null)} data-testid="hr-tpl-cancel">Cancel</Button>
          </div>
          {preview && (
            <div className="bg-white text-[#1A1A1A] rounded-lg p-6 shadow-xl max-h-[50vh] overflow-y-auto" data-testid="hr-tpl-preview-pane">
              <pre className="whitespace-pre-wrap font-sans text-[12px] leading-relaxed">{previewText}</pre>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border shadow-none" data-testid="hr-templates-panel">
      <CardHeader>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="font-heading text-lg">HR document templates</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Per venue — used for offer letters and employment contracts in People → Onboarding. More document types (warnings, disciplinary) will reuse these templates over time.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => setEdit({ doc_type: "offer_letter", name: "", body: "" })} data-testid="hr-tpl-new">
            <Plus size={14} className="mr-1" /> New template
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {tpls.map((t) => (
          <div key={t.id} className="flex items-center justify-between gap-3 border-b last:border-0 pb-2" data-testid={`hr-tpl-row-${t.id}`}>
            <div>
              <p className="text-sm font-medium">{t.name} <span className="text-xs text-muted-foreground">v{t.version}</span></p>
              <p className="text-xs text-muted-foreground">{HR_DOC_LABELS[t.doc_type] || t.doc_type}{t.is_default ? " · built-in default" : ""}</p>
            </div>
            <div className="flex gap-1.5">
              {t.doc_type in HR_DOC_LABELS && (
                <Button size="sm" variant="ghost" className="text-xs h-7" onClick={() => restore(t)} data-testid={`hr-tpl-restore-${t.id}`}>Restore default</Button>
              )}
              <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setEdit({ id: t.id, doc_type: t.doc_type, name: t.name, body: t.body })} data-testid={`hr-tpl-edit-${t.id}`}>
                <PencilSimple size={13} className="mr-1" /> Edit
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
